// Search (⌘K, and the phone's search button): projects, and sessions and entries of the last 30 days.
import { useMemo, useState } from 'react';
import { dayRange } from '../lib/calendar.ts';
import { navigate } from '../lib/router.ts';
import { activeProjects, displayTitle, providerInfo } from '../model/data.ts';
import { Sheet, TextInput } from '../components/common.tsx';
import { useFmt, useNow, useRange, useStore } from '../store/hooks.tsx';
import { Icon, type IconName } from '../ui/Icon.tsx';

interface Hit {
  key: string;
  icon: IconName;
  color?: number;
  title: string;
  sub: string;
  go: () => void;
}

export function SearchSheet() {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const now = useNow(60_000);
  const [query, setQuery] = useState('');
  const range = useMemo(() => ({ start: dayRange(now, store.timeZone, -29).start, end: dayRange(now, store.timeZone).end }), [Math.floor(now / 3_600_000), store.timeZone]);
  const data = useRange(range).data;
  const close = () => store.closeSheet();
  const q = query.trim().toLowerCase();
  const hits: Hit[] = [];
  if (q) {
    for (const p of activeProjects(store.ws)) {
      if (p.name.toLowerCase().includes(q)) hits.push({ key: `p${p.id}`, icon: 'folder', color: p.colorSlot, title: p.name, sub: t('Projekt'), go: () => navigate({ name: 'project', id: p.id }) });
    }
    for (const s of [...(data?.sessions ?? [])].reverse()) {
      const text = [displayTitle(s), s.repoName ?? '', s.gitBranch ?? '', providerInfo(s.provider).name].join(' ').toLowerCase();
      if (text.includes(q)) hits.push({ key: `s${s.id}`, icon: 'terminal', title: displayTitle(s), sub: `${providerInfo(s.provider).name} · ${fmt.started(s.startedAt, now)}`, go: () => navigate({ name: 'session', id: s.id }) });
    }
    for (const e of [...(data?.entries ?? [])].reverse()) {
      if (e.description.toLowerCase().includes(q) || e.tags.some((tag) => tag.toLowerCase().includes(q))) {
        hits.push({ key: `e${e.id}`, icon: 'user', title: e.description || t('Ohne Beschreibung'), sub: `${t('Eintrag')} · ${fmt.started(e.startAt, now)}`,
          go: () => store.openSheet({ kind: 'entry', entryId: e.id, day: null }) });
      }
    }
  }
  const shown = hits.slice(0, 12);
  const open = (hit: Hit) => {
    close();
    hit.go();
  };
  return (
    <Sheet title={t('Suchen')} onClose={close} width={560}>
      <span className="search-field search-field--wide">
        <Icon name="search" size={16} />
        <TextInput autoFocus type="search" value={query} placeholder={t('Projekte, Sessions, Einträge …')} aria-label={t('Suchen')}
          onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && shown[0]) open(shown[0]); }} />
      </span>
      {q && shown.length === 0 ? <p className="note">{t('Keine Treffer in den letzten 30 Tagen.')}</p> : null}
      <div className="search-hits" role="list">
        {shown.map((hit) => (
          <button key={hit.key} type="button" role="listitem" className="row-button search-hit" onClick={() => open(hit)}>
            {hit.color ? <span className="tl-dot" style={{ background: `var(--project-${hit.color})`, margin: '0 5px' }} /> : <Icon name={hit.icon} size={18} />}
            <span className="search-hit__text"><span className="search-hit__title">{hit.title}</span><span className="note">{hit.sub}</span></span>
            <Icon name="chevron" size={14} />
          </button>
        ))}
      </div>
    </Sheet>
  );
}
